import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import {
  ClientActivitiesValidators,
  ClientActivityFormValues,
  ClientRange,
  DeliveryChannelFormValues,
  DeliveryChannelValidators,
  ProductFormValues,
  ProductsValidators,
  RiskScoreItem,
  SourceOfFundsFormValues,
  SourceOfFundsValidators,
  ClientRangeFormValues,
} from '../../types';

export interface ProductsValidatorsResponse {
  data: ProductsValidators;
}

export interface DeliveryChannelValidatorsResponse {
  data: DeliveryChannelValidators;
}

export interface ClientActivitiesValidatorsResponse {
  data: ClientActivitiesValidators;
}

export interface SourceOfFundsValidatorsResponse {
  data: SourceOfFundsValidators;
}

export const individualClientsRiskApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIndividualVolumes: build.query<Response<ClientRange>, string>({
      query: (query) => ({
        url: `/v1/settings-clients-ranges${query}&all=true&clientType=INDIVIDUAL`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualVolumes: build.mutation<
      {
        data: ClientRange;
      },
      ClientRangeFormValues
    >({
      query: (body: ClientRangeFormValues) => ({
        url: '/v1/settings-clients-ranges',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualVolumes: build.mutation<
      {
        data: ClientRange;
      },
      { id: string; body: ClientRangeFormValues }
    >({
      query: (data) => ({
        url: `/v1/settings-clients-ranges/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteIndividualVolumes: build.mutation<
      {
        count: number;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/settings-clients-ranges/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualVolume: build.query<{ data: ClientRange }, string>({
      query: (id) => ({
        url: `/v1/settings-clients-ranges/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProductsValidators: build.query<ProductsValidatorsResponse, void>({
      query: () => ({
        url: '/v1/products/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProductsIndividual: build.query<Response<RiskScoreItem>, string>({
      query: (query) => ({
        url: `/v1/products${query}&clientType=INDIVIDUAL`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualProduct: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/products/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualProduct: build.mutation<
      {
        data: RiskScoreItem;
      },
      ProductFormValues
    >({
      query: (body: ProductFormValues) => ({
        url: '/v1/products',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualProduct: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: ProductFormValues; id: string }
    >({
      query: (data: { body: ProductFormValues; id: string }) => ({
        url: `/v1/products/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getDeliveryChannelValidators: build.query<
      DeliveryChannelValidatorsResponse,
      void
    >({
      query: () => ({
        url: '/v1/delivery-channels/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualDeliveryChannels: build.query<Response<RiskScoreItem>, string>(
      {
        query: (query) => ({
          url: `/v1/delivery-channels${query}&clientType=INDIVIDUAL`,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getIndividualDeliveryChannel: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/delivery-channels/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualDeliveryChannel: build.mutation<
      {
        data: RiskScoreItem;
      },
      DeliveryChannelFormValues
    >({
      query: (body: DeliveryChannelFormValues) => ({
        url: '/v1/delivery-channels',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualDeliveryChannel: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: DeliveryChannelFormValues; id: string }
    >({
      query: (data: { body: DeliveryChannelFormValues; id: string }) => ({
        url: `/v1/delivery-channels/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientsActivitiesValidators: build.query<
      ClientActivitiesValidatorsResponse,
      void
    >({
      query: () => ({
        url: '/v1/clients-activities/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientsActivitiesList: build.query<Response<RiskScoreItem>, string>({
      query: (query) => ({
        url: `/v1/clients-activities${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientsActivity: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/clients-activities/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createClientActivity: build.mutation<
      {
        data: RiskScoreItem;
      },
      ClientActivityFormValues
    >({
      query: (body: ClientActivityFormValues) => ({
        url: '/v1/clients-activities',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateClientActivity: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: ClientActivityFormValues; id: string }
    >({
      query: (data: { body: ClientActivityFormValues; id: string }) => ({
        url: `/v1/clients-activities/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSourceOfFundsValidators: build.query<
      SourceOfFundsValidatorsResponse,
      void
    >({
      query: () => ({
        url: '/v1/sources-of-funds/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSourceOfFundsList: build.query<Response<RiskScoreItem>, string>({
      query: (query) => ({
        url: `/v1/sources-of-funds${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSourceOfFunds: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/sources-of-funds/${id}`,
      }),
    }),
    createSourceOfFunds: build.mutation<
      {
        data: RiskScoreItem;
      },
      SourceOfFundsFormValues
    >({
      query: (body: SourceOfFundsFormValues) => ({
        url: '/v1/sources-of-funds',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateSourceOfFunds: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: SourceOfFundsFormValues; id: string }
    >({
      query: (data: { body: SourceOfFundsFormValues; id: string }) => ({
        url: `/v1/sources-of-funds/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetIndividualVolumesQuery,
  useGetProductsValidatorsQuery,
  useLazyGetIndividualProductQuery,
  useLazyGetProductsIndividualQuery,
  useCreateIndividualProductMutation,
  useUpdateIndividualProductMutation,
  useLazyGetDeliveryChannelValidatorsQuery,
  useLazyGetIndividualDeliveryChannelQuery,
  useLazyGetIndividualDeliveryChannelsQuery,
  useCreateIndividualDeliveryChannelMutation,
  useUpdateIndividualDeliveryChannelMutation,
  useLazyGetClientsActivitiesListQuery,
  useLazyGetClientsActivitiesValidatorsQuery,
  useLazyGetClientsActivityQuery,
  useCreateClientActivityMutation,
  useUpdateClientActivityMutation,
  useLazyGetSourceOfFundsListQuery,
  useLazyGetSourceOfFundsQuery,
  useLazyGetSourceOfFundsValidatorsQuery,
  useCreateSourceOfFundsMutation,
  useUpdateSourceOfFundsMutation,
  useCreateIndividualVolumesMutation,
  useLazyGetIndividualVolumeQuery,
  useUpdateIndividualVolumesMutation,
  useBatchDeleteIndividualVolumesMutation,
} = individualClientsRiskApi;

export const {
  endpoints: {
    getIndividualVolumes,
    getIndividualProduct,
    getProductsIndividual,
    getProductsValidators,
    createIndividualProduct,
    updateIndividualProduct,
    getDeliveryChannelValidators,
    getIndividualDeliveryChannel,
    getIndividualDeliveryChannels,
    createIndividualDeliveryChannel,
    updateIndividualDeliveryChannel,
    getClientsActivitiesList,
    getClientsActivitiesValidators,
    getClientsActivity,
    createClientActivity,
    updateClientActivity,
    getSourceOfFunds,
    getSourceOfFundsList,
    getSourceOfFundsValidators,
    createSourceOfFunds,
    updateSourceOfFunds,
    createIndividualVolumes,
    getIndividualVolume,
    updateIndividualVolumes,
    batchDeleteIndividualVolumes,
  },
} = individualClientsRiskApi;
